import { useEffect, useMemo } from 'react'

import type { ErrorLike } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'

import { useCallbackRef } from '@buffer-mono/popcorn'

import { buildTagComparator } from '~publish/components/FilterByTag'
import { graphql } from '~publish/graphql'
import type { FilterablePostType } from '~publish/helpers/postType'
import { usePageVisibilityRefresh } from '~publish/hooks/usePageVisibilityRefresh'
import { registerPostCountsFetcher } from '~publish/services/postCounts'
import { PusherEvent, usePusherEvent } from '~publish/services/pusher'

import { makeUseGlobalStateHook } from './useGlobalState'

const allPostEvents = [
  PusherEvent.POST_CREATED,
  PusherEvent.POST_UPDATED,
  PusherEvent.POST_DELETED,
  PusherEvent.POST_SENT,
  PusherEvent.POST_TAG_ADDED,
  PusherEvent.POST_TAG_REMOVED,
  PusherEvent.DRAFT_MOVED,
  PusherEvent.DRAFT_UPDATED,
  PusherEvent.DRAFT_APPROVED,
  PusherEvent.QUEUE_CHANGED,
]

export const GetPostCountsByChannel = graphql(/* GraphQL */ `
  query GetPostCountsByChannel(
    $organizationId: OrganizationId!
    $channelIds: [ChannelId!]
    $tags: TagComparator
    $postTypes: [PostType!]
  ) {
    drafts: postCounts(
      input: {
        organizationId: $organizationId
        filter: {
          channelIds: $channelIds
          tags: $tags
          postTypes: $postTypes
          status: [draft]
        }
      }
    ) {
      counts {
        channelId
        count
        limit
      }
    }

    queue: postCounts(
      input: {
        organizationId: $organizationId
        filter: {
          channelIds: $channelIds
          tags: $tags
          postTypes: $postTypes
          status: [scheduled, error]
        }
      }
    ) {
      counts {
        channelId
        count
        limit
      }
    }

    approvals: postCounts(
      input: {
        organizationId: $organizationId
        filter: {
          channelIds: $channelIds
          tags: $tags
          postTypes: $postTypes
          status: [needs_approval]
        }
      }
    ) {
      counts {
        channelId
        count
        limit
      }
    }

    sent: postCounts(
      input: {
        organizationId: $organizationId
        filter: {
          channelIds: $channelIds
          tags: $tags
          postTypes: $postTypes
          status: [sent, sending]
        }
      }
    ) {
      counts {
        channelId
        count
        limit
      }
    }
  }
`)

type PostCountsValues = {
  drafts: number
  approvals: number
  sent: number
  queue: number
  limit: number | null
}

type CountsByChannel = Record<string, PostCountsValues>
type ApolloCountsResult<T> = {
  counts: T
  loading: boolean
  error?: ErrorLike | null
}

const defaultCacheValue: Omit<
  ApolloCountsResult<CountsByChannel>,
  'loading'
> = {
  error: null,
  counts: {},
}
const usePostsCountByChannelCache = makeUseGlobalStateHook<
  Record<string, ApolloCountsResult<CountsByChannel>>
>({})
const loadingQueries = new Set<string>()
// Cache keys that changed while the page was hidden. Module-level like
// `loadingQueries`, so consumers sharing a key refetch once between them.
const staleQueries = new Set<string>()

type PostCountsProps = {
  organizationId: string | undefined | null
  channelIds?: string[]
  tagIds?: string[]
  /** Post formats to count by. Omit or pass an empty list for every format. */
  postTypes?: readonly FilterablePostType[]
}

const getEmptyCounts = (): PostCountsValues => ({
  drafts: 0,
  approvals: 0,
  sent: 0,
  queue: 0,
  limit: null,
})

// Always fetches counts for every channel in the organization, so all
// consumers share one cache key. `usePostCounts` filters by channel afterwards.
const usePostCountsByChannel = (
  props: Omit<PostCountsProps, 'channelIds'>,
): ApolloCountsResult<CountsByChannel> => {
  const { organizationId, tagIds, postTypes } = props
  const variables = useMemo(
    () => ({
      organizationId,
      tags: buildTagComparator(tagIds),
      postTypes: postTypes?.length ? [...postTypes] : undefined,
    }),
    [organizationId, tagIds, postTypes],
  )
  const [getPostCounts] = useLazyQuery(GetPostCountsByChannel, {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error - organizationId is not always defined we skip if it is not defined
    variables,
    // caching breaks for this query as all 'totalCount' fragments
    // are cached in an overlapping manner
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    skip: !organizationId,
  })

  const cacheKey = useMemo(() => JSON.stringify(variables), [variables])

  const [cache, setCounts] = usePostsCountByChannelCache()

  const fetchAndUpdateCache = useCallbackRef(async () => {
    const { organizationId: queryOrganizationId } = variables

    if (loadingQueries.has(cacheKey) || !queryOrganizationId) {
      return
    }

    loadingQueries.add(cacheKey)
    try {
      const { data, error } = await getPostCounts({
        variables: { ...variables, organizationId: queryOrganizationId },
      })

      const newState: CountsByChannel = {}
      for (const key of ['drafts', 'approvals', 'sent', 'queue'] as const) {
        for (const value of data?.[key]?.counts ?? []) {
          newState[value.channelId] =
            newState[value.channelId] ?? getEmptyCounts()
          newState[value.channelId][key] += value.count
          const oldLimit = newState[value.channelId].limit
          const newLimit = value.limit ?? null
          newState[value.channelId].limit = oldLimit ?? null
          if (newLimit !== null && oldLimit === null) {
            newState[value.channelId].limit = newLimit
          } else if (newLimit !== null && oldLimit !== null) {
            // always use the lower limit. This may be applied at the BE's discretion
            // and may be simply added to reduce load or to avoid an expensive query
            newState[value.channelId].limit = Math.min(oldLimit, newLimit)
          }
        }
      }

      // update with loaded data
      setCounts((previous) => ({
        ...previous,
        [cacheKey]: {
          counts: newState,
          loading: false,
          error,
        },
      }))
      return newState
    } finally {
      loadingQueries.delete(cacheKey)
    }
  })

  useEffect(() => {
    if (cacheKey in cache) return
    fetchAndUpdateCache()
  }, [cache, cacheKey, fetchAndUpdateCache])

  useEffect(
    () => registerPostCountsFetcher(fetchAndUpdateCache),
    [fetchAndUpdateCache],
  )

  // Post events are org-wide, so a hidden tab would refetch on every one of
  // them. Defer to the next time the page is visible instead.
  usePusherEvent(allPostEvents, () => {
    if (document.visibilityState === 'hidden') {
      staleQueries.add(cacheKey)
      return
    }
    fetchAndUpdateCache()
  })

  const refetchStaleCounts = useCallbackRef(() => {
    staleQueries.delete(cacheKey)
    fetchAndUpdateCache()
  })
  const hasStaleCounts = useCallbackRef(() => staleQueries.has(cacheKey))
  usePageVisibilityRefresh(refetchStaleCounts, hasStaleCounts)

  return (
    cache[cacheKey] ?? {
      ...defaultCacheValue,
      loading: true,
    }
  )
}

export type UsePostsCountResult = ApolloCountsResult<PostCountsValues | null>

export const usePostCounts = (props: PostCountsProps): UsePostsCountResult => {
  const result = usePostCountsByChannel({
    organizationId: props.organizationId,
    tagIds: props.tagIds,
    postTypes: props.postTypes,
  })
  const counts = useMemo(() => {
    const totals = getEmptyCounts()
    const byChannel = Object.entries(result.counts)
    if (byChannel.length === 0) return null
    for (const [channelId, value] of byChannel) {
      const included =
        props.channelIds?.includes(channelId) ||
        props.channelIds?.length === 0 ||
        props.channelIds === undefined
      if (!included) continue
      for (const key of ['drafts', 'approvals', 'sent', 'queue'] as const) {
        totals[key] += value[key]
      }
      totals.limit = value.limit ?? null
    }
    return totals
  }, [result.counts, props.channelIds])

  return {
    ...result,
    counts,
  }
}
